import { useState } from "react";
import { mapSprSummary } from "../../services/mappers/sprEntryMapper";
import {
  SPR_VERSION,
  buildSprReviewUrl,
  buildSprSentPreviewUrl,
} from "../../services/utils/constants";

/**
 * State and handlers for the SPR entry block. No fetch of its own: the host screen's
 * course row already carries the real `spr` summary — GET /mentors/studentManagment/dashboard
 * `studentList[].courses[].spr` (see sprEntryMapper). The host owns the course: `courseId`
 * and `spr` always come from props, and the block's own dropdown (LM profile) reports changes
 * through the host's `onCourseChange`, so every course dropdown on the page stays in step.
 *
 * `spr` per the BE contract: `undefined` (key absent) = SPR is off for this user → render
 * nothing; `null` = this course's subject has no SPR; an object = show the cards. A null date
 * (`autoBuiltAt` before the report's first build, `draftUpdatedAt`) hides that card's date line.
 *
 * Both buttons open the review page in a new tab with `version=auto|draft`; that page loads the
 * report with POST /spr-details/v2, and its `reportId` (the system report's for auto, the
 * draft's — the same id as `spr.draftId` — for draft) is what its saves address.
 *
 * "View history" (shown only when `spr.lastSentAt` is set) opens the SPR History popup; its
 * preview icons open one send (`/spr-sent/:sentId`) in a new tab.
 *
 * @param {{ studentId: string, courseId: string, spr: Object|null|undefined }} args
 */
const useSprStatusBlock = ({ studentId, courseId, spr }) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const openReview = (version) =>
    window.open(buildSprReviewUrl({ studentId, courseId, version }), "_blank");

  return {
    state: { summary: spr ? mapSprSummary(spr) : null, isHistoryOpen },
    derived: {
      hasCourse: !!courseId,
      // Key absent: BE has the feature off for this user.
      isFeatureOff: spr === undefined,
      hasNoReport: spr === null,
    },
    handlers: {
      onReviewAutomated: () => openReview(SPR_VERSION.AUTO),
      onContinueDraft: () => openReview(SPR_VERSION.DRAFT),
      onOpenHistory: () => setIsHistoryOpen(true),
      onCloseHistory: () => setIsHistoryOpen(false),
      onPreviewSent: (sentId) => window.open(buildSprSentPreviewUrl(sentId), "_blank"),
    },
  };
};

export default useSprStatusBlock;
